import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { PlusIcon, PlayIcon, PauseIcon, PencilIcon, TrashIcon, CloudArrowUpIcon, KeyIcon, XMarkIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

import { FlowInfo } from '../types';
import { FlowAPI } from '../services/api';
import LoadingSpinner from './LoadingSpinner';

const FlowDashboard = () => {
  const [flows, setFlows] = useState<FlowInfo[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [newFlowName, setNewFlowName] = useState('');
  const [newFlowDesc, setNewFlowDesc] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const importInputRef = useRef<HTMLInputElement>(null);

  // State for keywords modal
  const [showKeywordsModal, setShowKeywordsModal] = useState(false);
  const [currentFlowForKeywords, setCurrentFlowForKeywords] = useState<FlowInfo | null>(null);
  const [triggerWords, setTriggerWords] = useState<string[]>([]);
  const [newWord, setNewWord] = useState('');
  const [isSavingWords, setIsSavingWords] = useState(false);
  const keywordInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    loadFlows();
  }, []);

  const loadFlows = async () => {
    try {
      setIsLoading(true);
      const flowList = await FlowAPI.listFlows();
      setFlows(flowList);
    } catch (error: any) {
      toast.error(error.message || 'Erro ao carregar fluxos');
    } finally {
      setIsLoading(false);
    }
  };

  const openKeywordsModal = async (flow: FlowInfo) => {
    setCurrentFlowForKeywords(flow);
    setShowKeywordsModal(true);
    try {
      const words = await FlowAPI.getTriggerWords(flow.id);
      setTriggerWords(words);
    } catch (error: any) {
      toast.error(error.message || 'Erro ao carregar palavras-chave');
      setTriggerWords([]);
    }
  };

  const handleAddWord = () => {
    const word = newWord.trim().toLowerCase();
    if (!word) return;
    if (triggerWords.includes(word)) {
      toast.error('Esta palavra-chave já existe');
      return;
    }
    setTriggerWords([...triggerWords, word]);
    setNewWord('');
    keywordInputRef.current?.focus();
  };

  const handleRemoveWord = (word: string) => {
    setTriggerWords(triggerWords.filter((w: string) => w !== word));
  };

  const handleKeywordKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddWord();
    }
  };

  const handleSaveTriggerWords = async () => {
    if (!currentFlowForKeywords) return;
    setIsSavingWords(true);
    try {
      await FlowAPI.updateTriggerWords(currentFlowForKeywords.id, triggerWords);
      toast.success('Palavras-chave salvas com sucesso!');
      // Atualiza apenas o fluxo afetado sem recarregar a lista toda (evita cards duplicados)
      setFlows(prev => prev.map(f =>
        f.id === currentFlowForKeywords.id ? { ...f, trigger_words: triggerWords } : f
      ));
      setShowKeywordsModal(false);
    } catch (error: any) {
      toast.error(error.message || 'Erro ao salvar palavras-chave');
    } finally {
      setIsSavingWords(false);
    }
  };

  const handleDeactivateFlow = async () => {
    if (!window.confirm('Deseja desativar o fluxo atual? O bot ficará sem fluxo ativo.')) return;
    const toastId = toast.loading('Desativando fluxo...');
    try {
      await FlowAPI.deactivateFlow();
      toast.success('Fluxo desativado com sucesso!', { id: toastId });
      loadFlows();
    } catch (error: any) {
      toast.error(error.message || 'Erro ao desativar fluxo', { id: toastId });
    }
  };

  const handleActivateFlow = async (flowId: string, flowName: string) => {
    if (!window.confirm(`Deseja ativar o fluxo "${flowName}"? O bot será reiniciado com esta nova configuração.`)) {
      return;
    }
    const toastId = toast.loading(`Ativando fluxo "${flowName}"...`);
    try {
      await FlowAPI.switchActiveFlow(flowId);
      toast.success(`Fluxo "${flowName}" ativado com sucesso!`, { id: toastId });
      loadFlows();
    } catch (error: any) {
      toast.error(error.message || 'Erro ao ativar fluxo', { id: toastId });
    }
  };

  const handleCreateFlow = async () => {
    if (!newFlowName.trim()) {
      toast.error('Informe um nome para o fluxo');
      return;
    }
    setIsCreating(true);
    try {
      await FlowAPI.createFlow(newFlowName.trim(), newFlowDesc.trim());
      toast.success('Fluxo criado com sucesso!');
      setShowCreateModal(false);
      setNewFlowName('');
      setNewFlowDesc('');
      loadFlows();
    } catch (error: any) {
      toast.error(error.message || 'Erro ao criar fluxo');
    } finally {
      setIsCreating(false);
    }
  };

  const handleDeleteFlow = async (flowId: string, flowName: string) => {
    if (!window.confirm(`Confirma exclusão do fluxo "${flowName}"? Esta ação não pode ser desfeita.`)) return;
    try {
      await FlowAPI.deleteFlow(flowId);
      toast.success('Fluxo excluído');
      loadFlows();
    } catch (error: any) {
      toast.error(error.message || 'Erro ao excluir fluxo');
    }
  };

  const handleImportTypebot = async (event: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = event.target.files?.[0];
    if (!file) return;
    setIsImporting(true);
    const toastId = toast.loading('Importando fluxo TypeBot...');
    try {
      await FlowAPI.importTypebot(file);
      toast.success('Fluxo TypeBot importado com sucesso!', { id: toastId });
      setShowImportModal(false);
      loadFlows();
    } catch (error: any) {
      toast.error(error.message || 'Erro ao importar fluxo', { id: toastId });
    } finally {
      setIsImporting(false);
      if (importInputRef.current) importInputRef.current.value = '';
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  const totalNodes = flows.reduce((sum, f) => sum + (f.node_count || 0), 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Dashboard de Fluxos</h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">Gerencie e ative seus fluxos de conversa.</p>
        </div>

        <div className="mt-4 sm:mt-0 flex flex-wrap gap-2">
          <button
            onClick={() => setShowImportModal(true)}
            className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            <CloudArrowUpIcon className="w-4 h-4 mr-2" />
            Importar TypeBot
          </button>

          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <PlusIcon className="w-4 h-4 mr-2" />
            Novo Fluxo
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
          <p className="text-sm text-gray-500 dark:text-gray-400">Total de Fluxos</p>
          <p className="text-3xl font-bold text-gray-900 dark:text-white mt-1">{flows.length}</p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
          <p className="text-sm text-gray-500 dark:text-gray-400">Total de Nós</p>
          <p className="text-3xl font-bold text-gray-900 dark:text-white mt-1">{totalNodes}</p>
        </div>
      </div>

       {/* WhatsApp Commands Section */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 border border-gray-200 dark:border-gray-700">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
            📱 Comandos para WhatsApp <span className="text-sm font-normal text-gray-500 ml-2">(Admin)</span>
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-4 bg-gray-50 dark:bg-gray-900/50 rounded-lg border border-gray-200 dark:border-gray-700">
            <h3 className="font-medium text-gray-900 dark:text-gray-200 mb-2 flex items-center">
                📤 Enviar Bloco/Fluxo
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                Envia blocos de mídia específicos para um número.
            </p>
            <code className="text-xs bg-gray-100 dark:bg-gray-800 p-2 rounded block break-all font-mono text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700">
              Enviar o bloco [NOME] para o número [TEL]
            </code>
            <p className="text-[10px] text-gray-400 mt-1 italic">
              Ex: Enviar o bloco 5k RP para o número 556798913244
            </p>
          </div>
          
          <div className="p-4 bg-gray-50 dark:bg-gray-900/50 rounded-lg border border-gray-200 dark:border-gray-700">
            <h3 className="font-medium text-gray-900 dark:text-gray-200 mb-2 flex items-center">
                📋 Listar Blocos
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
              Mostra todos os blocos de mídia disponíveis para envio.
            </p>
            <code className="text-xs bg-gray-100 dark:bg-gray-800 p-2 rounded block font-mono text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700">
              Listar blocos
            </code>
          </div>

          <div className="p-4 bg-gray-50 dark:bg-gray-900/50 rounded-lg border border-gray-200 dark:border-gray-700">
            <h3 className="font-medium text-gray-900 dark:text-gray-200 mb-2 flex items-center">
                🚫 Cancelar Envio
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
              Interrompe imediatamente o envio de mídias em andamento.
            </p>
            <code className="text-xs bg-gray-100 dark:bg-gray-800 p-2 rounded block font-mono text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700">
              Cancelar envio
            </code>
          </div>
        </div>
      </div>

      {/* Flow List */}
      {flows.length === 0 ? (
        <div className="text-center py-16 bg-white dark:bg-gray-800 rounded-lg border border-dashed border-gray-300 dark:border-gray-600">
          <p className="text-gray-500 dark:text-gray-400 mb-4">Nenhum fluxo encontrado.</p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <PlusIcon className="w-4 h-4 mr-2" />
            Criar Primeiro Fluxo
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {flows.map((flow) => (
            <div
              key={flow.id}
              className={`bg-white dark:bg-gray-800 rounded-lg border hover:shadow-lg transition-shadow ${
                flow.is_active ? 'border-green-400 ring-2 ring-green-200 dark:ring-green-900' : 'border-gray-200 dark:border-gray-700'
              }`}
            >
              <div className="p-6">
                <div className="flex items-start justify-between mb-2">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{flow.name}</h3>
                  {flow.is_active && (
                    <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700 whitespace-nowrap">
                      <CheckCircleIcon className="w-3 h-3 mr-1" /> Ativo
                    </span>
                  )}
                </div>

                {flow.description && (
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-3 line-clamp-2">{flow.description}</p>
                )}

                {/* Palavras-chave do fluxo */}
                <div className="mb-3">
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Palavras-chave:</p>
                  <div className="flex flex-wrap gap-1">
                    {flow.trigger_words && flow.trigger_words.length > 0 ? (
                      flow.trigger_words.map(w => (
                        <span key={w} className="inline-block bg-yellow-100 text-yellow-700 text-xs font-medium px-2 py-0.5 rounded-full border border-yellow-200">
                          "{w}"
                        </span>
                      ))
                    ) : (
                      <span className="text-xs text-gray-400 dark:text-gray-500 italic">Nenhuma</span>
                    )}
                  </div>
                </div>

                <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400 mb-4">
                  <span>{flow.node_count} nós</span>
                  <span className="capitalize text-xs bg-gray-100 dark:bg-gray-700 dark:text-gray-300 px-2 py-0.5 rounded">{flow.type}</span>
                </div>

                {/* Botões de Ação */}
                <div className="space-y-2">
                  <div className="flex space-x-2">
                    <Link
                      to={`/flow/${flow.id}/edit`}
                      className="flex-1 inline-flex items-center justify-center px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                      <PencilIcon className="w-4 h-4 mr-2" />
                      Editar
                    </Link>
                    <button
                      onClick={() => openKeywordsModal(flow)}
                      className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                      title="Editar palavras-chave"
                    >
                      <KeyIcon className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteFlow(flow.id, flow.name)}
                      className="inline-flex items-center px-3 py-2 border border-red-300 dark:border-red-700 rounded-md text-sm font-medium text-red-700 dark:text-red-400 bg-white dark:bg-gray-800 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                      title="Excluir fluxo"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </div>
                  {flow.is_active ? (
                    <button
                      onClick={handleDeactivateFlow}
                      className="w-full inline-flex items-center justify-center px-3 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-red-600 hover:bg-red-700 transition-colors"
                    >
                      <PauseIcon className="w-4 h-4 mr-2" />
                      Desativar Fluxo
                    </button>
                  ) : (
                    <button
                      onClick={() => handleActivateFlow(flow.id, flow.name)}
                      className="w-full inline-flex items-center justify-center px-3 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-green-600 hover:bg-green-700 transition-colors"
                    >
                      <PlayIcon className="w-4 h-4 mr-2" />
                      Ativar Fluxo
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Keywords Modal */}
      {showKeywordsModal && currentFlowForKeywords && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                <KeyIcon className="w-5 h-5 text-yellow-500" />
                Palavras-chave de "{currentFlowForKeywords.name}"
              </h2>
              <button onClick={() => setShowKeywordsModal(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Este fluxo será iniciado se a primeira mensagem do lead contiver uma destas palavras/frases.
            </p>

            <div className="flex flex-wrap gap-2 mb-4 min-h-[40px] p-3 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600">
              {triggerWords.length === 0 && (
                <span className="text-sm text-gray-400 italic">Nenhuma palavra-chave adicionada</span>
              )}
              {triggerWords.map(w => (
                <span key={w} className="inline-flex items-center gap-1 bg-yellow-100 dark:bg-yellow-900/40 text-yellow-800 dark:text-yellow-300 text-sm font-medium px-3 py-1 rounded-full border border-yellow-300 dark:border-yellow-700">
                  {w}
                  <button onClick={() => handleRemoveWord(w)} className="text-yellow-600 hover:text-red-600 ml-1">
                    <XMarkIcon className="w-3.5 h-3.5" />
                  </button>
                </span>
              ))}
            </div>

            <div className="flex gap-2 mb-6">
              <input
                ref={keywordInputRef}
                type="text"
                value={newWord}
                onChange={e => setNewWord(e.target.value)}
                onKeyDown={handleKeywordKeyDown}
                placeholder='Ex: "oi", "quero correr"'
                className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-yellow-400"
                autoFocus
              />
              <button
                onClick={handleAddWord}
                disabled={!newWord.trim()}
                className="px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 disabled:opacity-40 transition-colors text-sm font-medium"
              >
                Adicionar
              </button>
            </div>

            <div className="flex justify-end gap-3">
              <button onClick={() => setShowKeywordsModal(false)} className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200">
                Cancelar
              </button>
              <button
                onClick={handleSaveTriggerWords}
                disabled={isSavingWords}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors font-medium"
              >
                {isSavingWords ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">Novo Fluxo</h2>
              <button onClick={() => setShowCreateModal(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Nome *</label>
                <input
                  type="text"
                  value={newFlowName}
                  onChange={e => setNewFlowName(e.target.value)}
                  placeholder="Ex: Fluxo de Vendas"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-400"
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Descrição</label>
                <textarea
                  value={newFlowDesc}
                  onChange={e => setNewFlowDesc(e.target.value)}
                  placeholder="Opcional..."
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setShowCreateModal(false)} className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200">
                Cancelar
              </button>
              <button
                onClick={handleCreateFlow}
                disabled={isCreating || !newFlowName.trim()}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors font-medium"
              >
                {isCreating ? 'Criando...' : 'Criar Fluxo'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Import Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">Importar TypeBot</h2>
              <button onClick={() => setShowImportModal(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Selecione um arquivo JSON exportado do TypeBot para importar como novo fluxo.
            </p>
            <input
              ref={importInputRef}
              type="file"
              accept=".json"
              onChange={handleImportTypebot}
              disabled={isImporting}
              className="block w-full text-sm text-gray-500 dark:text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-blue-50 dark:file:bg-blue-900/30 file:text-blue-700 dark:file:text-blue-300 hover:file:bg-blue-100"
            />
            {isImporting && <p className="text-sm text-blue-600 mt-2">Importando...</p>}
            <div className="flex justify-end mt-6">
              <button onClick={() => setShowImportModal(false)} className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200">
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FlowDashboard;
